import type { MessageDescriptor } from 'react-intl-next';

import { archiveFlagI18n, unarchiveFlagI18n, i18n } from './flagI18n';
import type { ArchiveStatusFlagI18n } from './archivingConstants';

export const EXPECTED_ERROR_MESSAGES = {
	// ** Archive validator messages:
	// (Generated by the Confluence Backend code here: https://stash.atlassian.com/projects/CONFCLOUD/repos/confluence/browse/confluence-core/confluence/src/java/com/atlassian/confluence/api/impl/service/content/ArchiveValidator.java)
	ALREADY_ARCHIVED:
		'Cannot archive pages because some or all pages in the input are already archived.',
	ALREADY_DELETED:
		'Cannot archive or update pages because some or all pages in the input are deleted.',
	IS_DRAFT: 'Cannot archive or update pages because some or all pages in the input are drafts.',
	ALREADY_UNARCHIVED_PAGES:
		"Cannot unarchive pages because some or all pages in the input aren't archived.",
	INVALID_CONTENT_FOR_UPDATE_NOTE:
		"Invalid arguments, can't edit the archive note of content that is unarchived.", // unused, resolves to default error
	MATCHING_PARENTPAGEID: "Invalid arguments, parentPageID can't match its child!",
	INVALID_PARENTPAGEID: 'Invalid arguments, parentPageID does not correspond to an existing page.',
	PERMISSION_CONTENTID:
		'User does not have permission to perform archiving operations on ContentID',
	SPACE_MISMATCH: "Invalid arguments. Can't unarchive a page to a different space.", // unused, resolves to default error
	INVALID_CONTENT: 'Incorrect arguments. One or more given Content IDs were not valid',

	// ** Mutation error messages:
	// (Generated by the Confluence Backend code here: https://stash.atlassian.com/projects/CONFCLOUD/repos/confluence/browse/confluence-core/confluence/src/java/com/atlassian/confluence/api/impl/service/content/PageArchiveServiceImpl.java)
	BULK_QUEUE_BUSY:
		'Could not submit request to SQS queue as another archival operation is already running.',
	ITEMS_WITH_INVALID_UNARCHIVE_PERMISSIONS_SUBMITTED:
		'User does not have permission to unarchive hierarchical to parentHierarchical',
	NOT_AUTHORIZED_TO_ARCHIVE: 'User not authorized to archive pageID',
	NOT_AUTHORIZED_TO_UNARCHIVE_TO_PARENT: 'User not authorized to unarchive pages to parentPageID',
	NOT_AUTHORIZED_TO_UNARCHIVE: 'User not authorized to unarchive pageID',
	NOT_AUTHORIZED_TO_EDIT_NOTE: 'User not authorized to edit archive note of Content ID', // unused, resolves to default error

	// (other mutation errors are things that should never happen and thus probably aren't correctable by the user)

	// ** Long Task error messages:
	// (Generated by the Confluence Backend code here: https://stash.atlassian.com/projects/CONFCLOUD/repos/confluence/browse/confluence-core/confluence/src/java/com/atlassian/confluence/api/impl/service/content/PageArchiveServiceImpl.java
	//  and here: https://stash.atlassian.com/projects/CONFCLOUD/repos/confluence/browse/confluence-core/confluence/src/java/com/atlassian/confluence/pages/bulk/archive/DefaultArchiveManager.java)

	// This error can occur if the content item is a draft, archived, or has already been deleted. Note
	// that we will likely change the code soon to support archived pages and drafts
	INVALID_CONTENT_STATUS_ARCHIVED: 'Hierarchical is already archived',
	INVALID_CONTENT_STATUS_DELETED: 'Hierarchical is deleted',
	INVALID_CONTENT_STATUS_DRAFT: 'Hierarchical is a draft',

	// (other long task errors are things that should never happen and thus probably aren't correctable by the user)

	// ** Long Task Timeout error message, generated by the FE code timeout
	LONG_TASK_TIMEOUT: 'Timed out while polling server for status of process',
};

// This function will translate internal error messages to user-facing internationalized message
// strings. If it receives an expected error, it chooses the matching user-facing message to
// display for that error. If it is not a expected error, then it will return the default user-
// facing message instead.
export const getArchiveErrorMessage = ({
	error,
	isArchive,
}: {
	error?: Error;
	isArchive: boolean;
}) => {
	let userMessage: MessageDescriptor | null = null;
	// If no error was specified or it doesn't have a message property, drop straight down to using
	// the default message
	const errorMessage = error?.message;
	if (errorMessage) {
		if (errorMessage.includes(EXPECTED_ERROR_MESSAGES.BULK_QUEUE_BUSY)) {
			userMessage = i18n.archiveErrorBulkQueueBusy;
		} else if (
			errorMessage.includes(EXPECTED_ERROR_MESSAGES.INVALID_CONTENT) ||
			errorMessage.includes(EXPECTED_ERROR_MESSAGES.MATCHING_PARENTPAGEID) ||
			errorMessage.includes(EXPECTED_ERROR_MESSAGES.INVALID_PARENTPAGEID)
		) {
			userMessage = isArchive
				? i18n.ARCHIVE_archiveErrorInvalidContentIds
				: i18n.UNARCHIVE_archiveErrorInvalidContentIds;
		} else if (
			errorMessage.includes(EXPECTED_ERROR_MESSAGES.INVALID_CONTENT_STATUS_ARCHIVED) ||
			errorMessage.includes(EXPECTED_ERROR_MESSAGES.ALREADY_ARCHIVED) ||
			errorMessage.includes(EXPECTED_ERROR_MESSAGES.ALREADY_UNARCHIVED_PAGES) ||
			errorMessage.includes(EXPECTED_ERROR_MESSAGES.INVALID_CONTENT_STATUS_DELETED) ||
			errorMessage.includes(EXPECTED_ERROR_MESSAGES.ALREADY_DELETED) ||
			errorMessage.includes(EXPECTED_ERROR_MESSAGES.INVALID_CONTENT_STATUS_DRAFT) ||
			errorMessage.includes(EXPECTED_ERROR_MESSAGES.IS_DRAFT)
		) {
			userMessage = i18n.archiveErrorInvalidContentStatusNotCurrent;
		} else if (
			errorMessage.includes(EXPECTED_ERROR_MESSAGES.PERMISSION_CONTENTID) ||
			errorMessage.includes(EXPECTED_ERROR_MESSAGES.NOT_AUTHORIZED_TO_ARCHIVE)
		) {
			userMessage = i18n.ARCHIVE_archiveErrorInvalidPermissions_Body;
		} else if (
			errorMessage.includes(
				EXPECTED_ERROR_MESSAGES.ITEMS_WITH_INVALID_UNARCHIVE_PERMISSIONS_SUBMITTED,
			) ||
			errorMessage.includes(EXPECTED_ERROR_MESSAGES.NOT_AUTHORIZED_TO_UNARCHIVE_TO_PARENT)
		) {
			userMessage = i18n.UNARCHIVE_archiveErrorInvalidPermissions_Body;
		} else if (errorMessage.includes(EXPECTED_ERROR_MESSAGES.LONG_TASK_TIMEOUT)) {
			userMessage = i18n.archiveErrorLongTaskTimeout;
		}
	}

	const defaultMessage = isArchive
		? i18n.ARCHIVE_archiveContentFlagFailure_Body
		: i18n.UNARCHIVE_archiveContentFlagFailure_Body;

	// If it is not one of the known messages, use the default message instead
	return userMessage || defaultMessage;
};

// This function returns another function that has the "isArchive" prop fixed.  This allows us to
// specify "isArchive" at the top level of the component tree and just pass the returned function
// down the component tree.
export const getArchiveFlagI18nWithErrorMsgs = ({
	isArchive,
}: {
	isArchive: boolean;
}): ArchiveStatusFlagI18n => {
	return {
		...(isArchive ? archiveFlagI18n : unarchiveFlagI18n),
		getErrorMessage: (error?: Error) => getArchiveErrorMessage({ error, isArchive }),
	};
};
